import { Dict, ModelId } from "../../../../defines";
import { ApiModel, ApiModelOptions } from "../../../ApiModel";
import {
  AddressModel,
  AttributeItemModel,
  ManagerModel,
  TrackRecordModel,
} from "../../../models";
import { JsonApiManyResponse } from "../../../types";
import apiAddressModel from "../useAddressModel/apiAddressModel";

export default function apiManagerModel(opts: ApiModelOptions<ManagerModel>) {
  const addressModel = apiAddressModel({
    apiClient: opts.apiClient,
    placementsApiClient: opts.placementsApiClient,
  });

  const model = ApiModel<ManagerModel>("manager", {
    forcePlacement: { update: true, delete: true },
    ...opts,
  });

  const processManagerData = async (
    data: ManagerModel,
    oldAddress?: AddressModel | null,
  ) => {
    const dataToSave = data;

    if (data.address) {
      const newAddress = await addressModel.saveAddressIfDifferent(
        data.address,
        oldAddress,
      );
      dataToSave.address = { id: newAddress?.id };
    }

    return dataToSave;
  };

  const updateAttributes = async (
    id: string | number,
    attributes: AttributeItemModel[],
  ) => {
    const attributesToSave = attributes.map((attr) => ({
      attribute_id: attr.id,
      is_primary_strategy: attr.isPrimaryStrategy,
      is_primary_property_type: attr.isPrimaryPropertyType,
    }));

    return await model.handler.rpcRequest("update-manager-attributes", {
      manager_id: parseInt(String(id)),
      attributes: attributesToSave,
    });
  };

  const getTrackRecord = async (
    idManager: string | number,
    queryParams?: Dict,
  ) => {
    const trackRecord = await model.handler.apiRequest<
      JsonApiManyResponse<TrackRecordModel>
    >(`/track-records/manager/${idManager}`, "GET", queryParams);

    return trackRecord?.data || [];
  };

  const sendBulkWelcomeEmails = async (managerId: ModelId) => {
    return await model.handler.post(
      "/mallcop/welcome-email/manager",
      {
        manager_id: Number(String(managerId)),
      },
      false,
    );
  };

  const update = async (
    id: ModelId,
    data: ManagerModel,
    params?: any,
    preloadExisting = true,
  ) => {
    const oldData = await model.getOne(id);
    const dataToSave = await processManagerData(data, oldData.data.address);
    return model.update(id, dataToSave, params, preloadExisting);
  };

  const createManager = async (newManagerName: string) => {
    const response = await model.apiClient.rpcRequest("create-manager-user", {
      manager_name: newManagerName,
    });

    if (response.error.message) {
      throw response.error.message;
    }

    const newManager = model.apiClient.transformAxios(response);
    return newManager.data as ManagerModel;
  };

  return {
    ...model,
    createManager,
    updateAttributes,
    getTrackRecord,
    sendBulkWelcomeEmails,
    update,
  };
}
